import { HttpClient } from '@angular/common/http'
import { Injectable } from '@angular/core'
import { Observable } from 'rxjs'
// import { environment } from '../../environments/environment'
import { eps } from './url.service'

export type NotificationChannel = 'push' | 'email' | 'both'
export type OccasionType = 'birthday' | 'work_anniversary' | 'other'

export type NotificationCampaignStatus =
  | 'scheduled'
  | 'queued'
  | 'running'
  | 'success'
  | 'partial_failed'
  | 'failed'
  | 'stopped'
  | 'cancelled'

export interface NotificationCampaign {
  id: string
  title: string
  message: string
  channel: NotificationChannel
  businessIds: string[]
  individualUserIds: string[]
  status: NotificationCampaignStatus
  totalRecipients: number
  processedRecipients: number
  pushSentCount: number
  pushFailedCount: number
  emailSentCount: number
  emailFailedCount: number
  scheduledAt?: string | null
  timezone?: string
  startedAt?: string | null
  completedAt?: string | null
  errorMessage?: string | null
  source?: string
  templateId?: string | null
  occasionDate?: string | null
  createdAt: string
}

export interface NotificationCampaignRecipient {
  id: string
  campaignId: string
  userId: string
  businessId: string | null
  businessName?: string
  pushStatus: 'pending' | 'sent' | 'failed' | 'skipped'
  pushError: string | null
  emailStatus: 'pending' | 'sent' | 'failed' | 'skipped'
  emailError: string | null
  user?: { firstName: string; lastName: string; email: string }
}

export interface CreateCampaignPayload {
  title: string
  message: string
  channel: NotificationChannel
  businessIds: string[]
  individualUserIds: string[]
  scheduledAt?: string
  timezone: string

  scheduleType?: 'once' | 'daily' | 'birthday' | 'work_anniversary'
  scheduleTime?: string | null
  isRecurring?: boolean
}

export interface OccasionTemplate {
  id: string
  businessId: string | null
  businessName?: string
  occasion: OccasionType
  ownerType: 'system' | 'business'
  title: string
  message: string
  channel: NotificationChannel
  cronExpression: string
  timezone: string
  isActive: boolean
  createdAt: string
}

export interface SaveTemplatePayload {
  occasion: OccasionType
  title: string
  message: string
  channel: NotificationChannel
  cronExpression?: string
  timezone?: string
  businessId?: string
}

@Injectable({ providedIn: 'root' })
export class NotificationCampaignService {
  //   private baseUrl = `${environment.apiUrl}/superAdmin/notification-campaign`
  //   private businessTemplateUrl = `${environment.apiUrl}/business/notification-template`

  private apiUrl = eps.notificationCampaign.listApi
  private notificationCampaignScheduleApiUrl =
    eps.notificationCampaign.notificationCampaignScheduleApi

  constructor(private http: HttpClient) {}

  // ---------------- Campaigns ----------------

  create(payload: CreateCampaignPayload): Observable<NotificationCampaign> {
    return this.http.post<NotificationCampaign>(this.apiUrl, payload)
  }

  list(params: { status?: string; page?: number; limit?: number }): Observable<any> {
    return this.http.get<any>(this.apiUrl, { params: params as any })
  }

  getOne(id: string): Observable<NotificationCampaign> {
    return this.http.get<NotificationCampaign>(`${this.apiUrl}${id}`)
  }

  getRecipients(
    id: string,
    params: {
      businessId?: string
      pushStatus?: string
      emailStatus?: string
      page?: number
      limit?: number
    }
  ): Observable<any> {
    return this.http.get<any>(`${this.apiUrl}${id}/recipients`, {
      params: params as any
    })
  }

  stop(id: string): Observable<any> {
    return this.http.post(`${this.notificationCampaignScheduleApiUrl}/${id}/stop`, {})
  }
  retry(id: string): Observable<any> {
    return this.http.post(`${this.notificationCampaignScheduleApiUrl}/${id}/retry`, {})
  }
  cancel(id: string): Observable<any> {
    return this.http.post(`${this.notificationCampaignScheduleApiUrl}/${id}/cancel`, {})
  }

  // ---------------- SuperAdmin: Occasion Templates ----------------

  createDefaultTemplate(payload: SaveTemplatePayload): Observable<OccasionTemplate> {
    return this.http.post<OccasionTemplate>(`${this.apiUrl}templates`, payload)
  }

  listTemplates(occasion?: OccasionType): Observable<OccasionTemplate[]> {
    return this.http.get<OccasionTemplate[]>(`${this.apiUrl}templates`, {
      params: occasion ? ({ occasion } as any) : {}
    })
  }

  deactivateTemplate(id: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/templates/${id}/deactivate`, {})
  }

  testRunTemplate(id: string): Observable<any> {
    return this.http.post(`${this.apiUrl}/templates/${id}/test-run`, {})
  }

  // ---------------- Business Owner: Occasion Template override ----------------

  getBusinessTemplates(): Observable<{
    birthday: { active: OccasionTemplate; isCustom: boolean }
    workAnniversary: { active: OccasionTemplate; isCustom: boolean }
  }> {
    return this.http.get<any>(this.notificationCampaignScheduleApiUrl)
  }

  saveBusinessTemplate(payload: SaveTemplatePayload): Observable<OccasionTemplate> {
    return this.http.post<OccasionTemplate>(this.notificationCampaignScheduleApiUrl, payload)
  }

  resetBusinessTemplateToDefault(occasion: OccasionType): Observable<any> {
    return this.http.post(
      `${this.notificationCampaignScheduleApiUrl}/${occasion}/reset-to-default`,
      {}
    )
  }
}
